import React, { useEffect, useMemo, useState } from "react";

import { Card, CardContent } from "@components/ui/card";
import { ECON_API_BASE_URL } from "@lib/config";
import {
  type EconLatestFeed,
  type RunCheck,
  type RunState,
  buildRunChecks,
  fmtMt,
  summarizeRuns,
} from "@lib/mlRunStatus";

// Model Runs: whether the economic model's scheduled Azure ML training run and daily scoring run landed,
// read from the run records they publish (src/lib/mlRunStatus.ts). Re-reads every 10 minutes and keeps
// the last result on screen while it does, so a tab left open over the 1st moves from Due to Ran.

const REFRESH_MS = 10 * 60_000;
const CLOCK_MS = 60_000;

const ui = {
  cardBg: "var(--card-bg)",
  cardBorder: "var(--card-border)",
  textPrimary: "var(--text-primary)",
  textSecondary: "var(--text-secondary)",
  textMuted: "var(--text-muted)",
  shadow: "var(--shadow-soft)",
};

const cardStyle: React.CSSProperties = {
  backgroundColor: ui.cardBg,
  border: `1px solid ${ui.cardBorder}`,
  color: ui.textPrimary,
  boxShadow: ui.shadow,
};

type Tone = "positive" | "warning" | "danger" | "info";

const STATE_CHIP: Record<RunState, { label: string; tone: Tone }> = {
  current: { label: "Ran", tone: "positive" },
  due: { label: "Due", tone: "info" },
  overdue: { label: "Overdue", tone: "warning" },
  error: { label: "Problem", tone: "danger" },
  missing: { label: "No record", tone: "danger" },
};

function chipStyle(tone: Tone): React.CSSProperties {
  return {
    color: `var(--status-${tone}-text)`,
    backgroundColor: `var(--status-${tone}-bg)`,
    border: `1px solid var(--status-${tone}-border)`,
  };
}

function Chip({ tone, children }: { tone: Tone; children: React.ReactNode }) {
  return (
    <span className="inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold" style={chipStyle(tone)}>
      {children}
    </span>
  );
}

async function readFeed<T>(path: string): Promise<T> {
  const res = await fetch(`${ECON_API_BASE_URL}${path}`, { cache: "no-store" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as T;
}

type Feed = { econ: EconLatestFeed; error: string | null };

const ModelRunsCard: React.FC = () => {
  const [feed, setFeed] = useState<Feed | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      let next: Feed;
      try {
        next = { econ: await readFeed<EconLatestFeed>("/econ/latest"), error: null };
      } catch (e: any) {
        next = { econ: null, error: String(e?.message || e) };
      }
      if (cancelled) return;
      setFeed(next);
      setNowMs(Date.now());
    };
    load();
    const refresh = window.setInterval(load, REFRESH_MS);
    const clock = window.setInterval(() => setNowMs(Date.now()), CLOCK_MS);
    return () => {
      cancelled = true;
      window.clearInterval(refresh);
      window.clearInterval(clock);
    };
  }, []);

  const checks = useMemo(() => (feed ? buildRunChecks(feed.econ, nowMs, feed.error) : null), [feed, nowMs]);
  const rows: RunCheck[] = checks ?? [];
  const summary = summarizeRuns(rows);

  let headline: React.ReactNode = null;
  if (checks) {
    if (summary.attention > 0) {
      headline = <Chip tone="warning">{summary.attention === 1 ? "1 run needs attention" : `${summary.attention} runs need attention`}</Chip>;
    } else if (summary.due > 0) {
      headline = <Chip tone="info">{summary.due === 1 ? "1 scheduled run due" : `${summary.due} scheduled runs due`}</Chip>;
    } else {
      headline = <Chip tone="positive">All {rows.length} runs current</Chip>;
    }
  }

  return (
    <div data-testid="ml-model-runs">
      <Card style={cardStyle}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h3 className="font-medium">Model Runs</h3>
              <p className="text-xs" style={{ color: ui.textMuted }}>
                Economic regime model. Training runs monthly in Azure ML on the 1st; daily scoring re-uses the latest
                model. A training run that fails publishes nothing, so it shows here as Overdue. The reason is in Azure
                ML studio.
              </p>
            </div>
            {headline}
          </div>

          {!checks ? (
            <p className="text-sm" style={{ color: ui.textMuted }}>Reading run records…</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide" style={{ color: ui.textMuted }}>
                    <th className="py-1.5 pr-4 font-medium">Run</th>
                    <th className="py-1.5 pr-4 font-medium">Last completed</th>
                    <th className="py-1.5 pr-4 font-medium">Status</th>
                    <th className="py-1.5 font-medium">Next scheduled</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => {
                    const chip = STATE_CHIP[c.state];
                    return (
                      <tr key={c.spec.key} data-run={c.spec.key} data-state={c.state} className="align-top" style={{ borderTop: `1px solid ${ui.cardBorder}` }}>
                        <td className="py-2 pr-4">
                          <div className="font-medium">{c.spec.step}</div>
                          <div className="text-xs whitespace-nowrap" style={{ color: ui.textMuted }} title={c.spec.source}>
                            {c.cadenceLabel}
                          </div>
                        </td>
                        <td className="py-2 pr-4 whitespace-nowrap" style={{ color: ui.textSecondary }}>{fmtMt(c.lastRunMs)}</td>
                        <td className="py-2 pr-4">
                          <Chip tone={chip.tone}>{chip.label}</Chip>
                          {c.note ? (
                            <div className="mt-1 text-xs max-w-xs" style={{ color: ui.textSecondary }}>{c.note}</div>
                          ) : null}
                        </td>
                        <td className="py-2 whitespace-nowrap" style={{ color: ui.textSecondary }}>{fmtMt(c.nextMs)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ModelRunsCard;
